import React from 'react';
import PropTypes from 'prop-types';
import { connect } from '@gasbuddy/react';
import { FlexGrid, Header } from '@gasbuddy/react-components';
import Card from '../card';
import BpDateRangePicker from '../bp-date-range-picker';
import ComparisonMetricSelector from '../comparison-metric-selector';
import StationPropType from '../../utils/prop-types/station';
import DateRangePropType from '../../utils/prop-types/date-range';
import ComparisonMetricPropType from '../../utils/prop-types/comparison-metric';

import BrandEngagementContent from './brand-engagement-content';
import RatingsPerformanceContent from './ratings-performance-content';
import useIsMounted from '../../hooks/useIsMounted';

import {
  getEngagement,
  getComparisonEngagement,
  getRatingCategories,
  getRatingsCategoryScore,
  getRatings,
  getComparisonRatings,
  getStationVisitsHourly,
} from './stats-helper';

import styles from './styles.css';
import RatingsCategoryScoreContent from './ratings-category-score-content';
import PeakVisitsContent from './peak-visits-content';

const { logError } = require('../../../lib/bputils').default;

function InsightsTabContent({ isLoading, station, dateRange, comparisonMetric }) {
  const [comparisonBrandEngagement, setComparisonBrandEngagement] = React.useState(null);
  const [ratingCategories, setRatingCategories] = React.useState(null);
  const [comparisonRatings, setComparisonRatings] = React.useState(null);

  const [brandEngagement, setBrandEngagement] = React.useState(null);
  const [brandEngagementError, setBrandEngagementError] = React.useState(false);
  const [ratingCategoryScores, setRatingCategoryScores] = React.useState(null);
  const [ratingCategoryScoresError, setRatingCategoryScoresError] = React.useState(false);
  const [ratings, setRatings] = React.useState(null);
  const [ratingsError, setRatingsError] = React.useState(false);
  const [stationVisits, setStationVisits] = React.useState(null);
  const [stationVisitsError, setStationVisitsError] = React.useState(false);

  const isMounted = useIsMounted();

  React.useEffect(() => {
    loadAllData().then(() => {
      if (isMounted.current) {
        loadRatingCategoryScore();
      }
    });
  }, [station]);

  React.useEffect(() => {
    loadAllData();
  }, [dateRange]);

  React.useEffect(() => {
    loadComparisonBrandEngagement();
    loadComparisonRatings();
  }, [comparisonMetric]);

  React.useEffect(() => {
    if (ratingCategories) {
      loadComparisonRatings();
    }
  }, [ratingCategories]);

  const loadAllData = async () => {
    loadBrandEngagement();
    loadComparisonBrandEngagement();
    loadPeakVisits();
    loadRatings();
  };

  const loadBrandEngagement = async () => {
    try {
      setBrandEngagement(null);
      if (!dateRange) {
        return;
      }
      const { id: stationId } = station;
      const { startDate, endDate } = dateRange;
      const brandEngagementResult = await getEngagement(stationId, startDate, endDate);
      setBrandEngagement(brandEngagementResult);
    } catch (err) {
      setBrandEngagementError(true);
      logError(err);
    }
  };

  const loadRatingCategoryScore = async () => {
    try {
      setRatingCategoryScores(null);
      const { id: stationId } = station;

      const ratingCategoryScoresResult = await getRatingsCategoryScore(stationId);
      setRatingCategoryScores(ratingCategoryScoresResult);
    } catch (err) {
      setRatingCategoryScoresError(true);
      logError(err);
    }
  };

  const loadRatings = async () => {
    try {
      setRatings(null);
      if (!dateRange) {
        return;
      }
      const { id: stationId } = station;
      const { startDate, endDate } = dateRange;

      if (!ratingCategories) {
        const newRatingsCategories = await getRatingCategories();
        setRatingCategories(newRatingsCategories);
      }
      const newRatings = await getRatings(stationId, startDate, endDate);
      setRatings(newRatings);
    } catch (err) {
      setRatingsError(true);
      logError(err);
    }
  };

  const loadComparisonBrandEngagement = async () => {
    try {
      if (!dateRange) {
        return;
      }

      setComparisonBrandEngagement(null);
      const { startDate, endDate } = dateRange;
      const { id: stationId, address: { state } } = station;

      const newComparisonBrandEngagement = await getComparisonEngagement(stationId, startDate, endDate, state, comparisonMetric);
      setComparisonBrandEngagement(newComparisonBrandEngagement);
    } catch (err) {
      setBrandEngagementError(true);
      logError(err);
    }
  };

  const loadComparisonRatings = async () => {
    try {
      setComparisonRatings(null);
      if (!dateRange || !ratingCategories) {
        return;
      }

      const { startDate, endDate } = dateRange;
      const { id: stationId, address: { state } } = station;


      const categoryIds = ratingCategories.map(rc => rc.id);
      const newComparisonRatings = await getComparisonRatings(
        stationId, categoryIds, startDate, endDate, state, comparisonMetric,
      );
      setComparisonRatings(newComparisonRatings);
    } catch (err) {
      setRatingsError(true);
      logError(err);
    }
  };

  const loadPeakVisits = async () => {
    try {
      setStationVisits(null);
      if (!dateRange) {
        return;
      }

      const { startDate, endDate } = dateRange;
      const { id: stationId } = station;

      const newStationVisits = await getStationVisitsHourly(stationId, startDate, endDate);
      setStationVisits(newStationVisits);
    } catch (err) {
      setStationVisitsError(true);
      logError(err);
    }
  };

  const renderRatingsCard = () => (
    <Card title="RATINGS PERFORMANCE" isLoading={isLoading || !ratingCategories || !ratings || !comparisonRatings} hasError={ratingsError}>
      <RatingsPerformanceContent
        stats={ratings}
        comparisonStats={comparisonRatings}
        ratingCategories={ratingCategories}
        comparisonMetricName={comparisonMetric?.text}
      />
    </Card>
  );

  const renderBrandEngagementCard = () => (
    <Card title="BRAND ENGAGEMENT" isLoading={isLoading || !brandEngagement || !comparisonBrandEngagement} hasError={brandEngagementError}>
      <BrandEngagementContent
        stats={brandEngagement}
        comparisonStats={comparisonBrandEngagement}
        comparisonMetricName={comparisonMetric?.text}
      />
    </Card>
  );

  const renderRatingsCategoryScoreCard = () => (
    <Card title="RATING CATEGORY SCORE" isLoading={isLoading || !ratingCategoryScores} hasError={ratingCategoryScoresError}>
      <RatingsCategoryScoreContent ratingCategoryScores={ratingCategoryScores} />
    </Card>
  );

  const renderPeakVisitsCard = () => (
    <Card title="PEAK VISITING TIMES" isLoading={isLoading || !stationVisits} hasError={stationVisitsError}>
      <PeakVisitsContent
        visitsPerHour={stationVisits?.visits || []}
        avgVisitDurationPerHour={stationVisits?.avg_visit_duration || []}
      />
    </Card>
  );

  return (
    <div className={styles.insightsTab}>
      <div className={styles.filterBar}>
        <div className={styles.filterItem}>
          <div>
            <Header as="h4">
                Compare To
            </Header>
          </div>
          <ComparisonMetricSelector
            id="forInsightsTab"
            key="forInsightsTab"
            className={styles.filterComponent}
            showBrandMetric
            gTagTitle="Insights Tab Comparison Metric Selector"
          />
        </div>
        <div className={styles.filterItem}>
          <div>
            <Header as="h4">
                Date Range
            </Header>
          </div>
          <BpDateRangePicker className={styles.filterComponent} gTagTitle="Insights Tab Date Range Picker" />
        </div>
      </div>

      <FlexGrid container>
        <FlexGrid.Column mobile={12} tablet={6}>
          {renderBrandEngagementCard()}
        </FlexGrid.Column>
        <FlexGrid.Column mobile={12} tablet={6}>
          {renderRatingsCategoryScoreCard()}
        </FlexGrid.Column>
        <FlexGrid.Column mobile={12} tablet={12}>
          {renderRatingsCard()}
        </FlexGrid.Column>
        <FlexGrid.Column mobile={12} tablet={12}>
          {renderPeakVisitsCard()}
        </FlexGrid.Column>
      </FlexGrid>
    </div>
  );
}

InsightsTabContent.propTypes = {
  isLoading: PropTypes.bool,
  station: StationPropType,
  dateRange: DateRangePropType,
  comparisonMetric: ComparisonMetricPropType,
};

InsightsTabContent.defaultProps = {
  isLoading: false,
  station: null,
  dateRange: null,
  comparisonMetric: null,
};

const mapStateToProps = state => ({
  station: state.station,
  dateRange: state.dateRange,
  isLoading: state.loading,
  comparisonMetric: state.comparisonMetric,
});

export default connect(mapStateToProps, null)(InsightsTabContent);
